import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Newspaper } from 'lucide-react';
import InteractiveArticleViewer from '../components/InteractiveArticleViewer';
import RelatedArticles from '../components/RelatedArticles';

/**
 * JSU Basketball 2026-27 Interactive Article Page
 * 
 * Features Daeshun Ruffin's return, SWAC Media Day, roster, coaches,
 * and full 2026-27 schedule with interactive elements.
 */
const JSUBasketballArticlePage = () => {
  const articleUrl = '/articles/jsu-basketball-2026-27.html';
  
  // Article metadata for social cards
  const articleMeta = {
    title: "Ruffin Returns: Jackson State Basketball Reloads for 2026-27",
    description: "Daeshun Ruffin returns as Jackson State prepares for SWAC Media Day. Full roster, coaches, and 2026-27 nonconference and SWAC schedules.",
    image: "https://customer-assets-39nsmqrw.emergentagent.net/job_hbcu-sports-hub/artifacts/dk0hy13n_B4DB57BB-AA18-44F2-A973-17BA0E7A6CA4.jpeg",
    url: "https://shelovesthee.com/jsu-basketball-2026-27",
    author: "Timona Stapleton | SheLovesThee",
    category: "Basketball"
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* SEO & Social Cards Meta Tags */}
      <Helmet>
        <title>{`${articleMeta.title} | SheLovesThee`}</title>
        <meta name="description" content={articleMeta.description} />
        <meta name="author" content={articleMeta.author} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={articleMeta.title} />
        <meta property="og:description" content={articleMeta.description} />
        <meta property="og:image" content={articleMeta.image} />
        <meta property="og:url" content={articleMeta.url} />
        <meta property="og:site_name" content="SheLovesThee" />
        <meta property="article:author" content={articleMeta.author} />
        <meta property="article:section" content={articleMeta.category} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={articleMeta.title} />
        <meta name="twitter:description" content={articleMeta.description} />
        <meta name="twitter:image" content={articleMeta.image} />
        <meta name="twitter:site" content="@SheLovesThee" />
      </Helmet>

      {/* Breaking News Banner */}
      <div className="bg-red-600 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
          <Newspaper className="w-5 h-5 animate-pulse" />
          <span className="font-bold text-sm uppercase tracking-wide">
            Breaking News: Daeshun Ruffin Returns to Jackson State Basketball
          </span>
          <Newspaper className="w-5 h-5 animate-pulse" />
        </div>
      </div>

      {/* Navigation Bar */}
      <div className="bg-[#071a3d] text-white py-3 px-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white hover:text-blue-300 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back to Home</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <Link 
              to="/news" 
              className="text-white hover:text-blue-300 transition-colors text-sm"
              data-testid="nav-news"
            >
              News
            </Link>
            <Link 
              to="/swac-pickem" 
              className="text-white hover:text-blue-300 transition-colors text-sm"
              data-testid="nav-pickem"
            >
              Pick'em
            </Link>
            <Link 
              to="/qb-analytics" 
              className="text-white hover:text-blue-300 transition-colors text-sm"
              data-testid="nav-qb"
            >
              QB Analytics
            </Link>
          </div>
        </div>
      </div>

      {/* Article Content */}
      <main className="pb-8">
        <InteractiveArticleViewer 
          articleUrl={articleUrl}
          title={articleMeta.title}
        />
        
        {/* Related Articles Section */}
        <div className="max-w-6xl mx-auto px-4 mt-12">
          <RelatedArticles 
            currentArticleId="jsu-basketball-2026-27"
            category="Basketball"
            tags={["jackson state", "basketball", "swac", "daeshun ruffin"]}
            maxArticles={4}
            title="More Basketball Coverage"
          />
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#071a3d] text-white py-6 text-center">
        <p className="text-gray-400 text-sm">
          SheLovesThee Sports Media • HBCU Sports. Real Talk. Thee I Love.
        </p>
        <p className="text-gray-500 text-xs mt-2">
          Photos courtesy of Jackson State Men's Basketball and Jackson State Athletics
        </p>
      </footer>
    </div>
  );
};

export default JSUBasketballArticlePage;
